import { AuthGuard } from "@elit-hair/elit-tools";
import { Button, Icon } from "@elit-hair/elit-ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbLoader2 } from "react-icons/tb";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { NotAuthorizedPage } from "@/pages/not-authorized/-index";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { ReportsWeekSelect } from "../reports/components/reports-week-select";
import { REPORT_SURFACE_CLASS } from "../reports/constants/report-ui";
import { getLastCompletedWeekAnchor } from "../reports/utils/get-last-completed-week-anchor";
import { DataExportDatasetSelect } from "./components/data-export-dataset-select";
import { DataExportResult } from "./components/data-export-result";
import { DATA_EXPORT_DATASETS, DATA_EXPORT_DATASET_ORDER } from "./constants/data-export.constants";
import { getDataExportDownloadUrl } from "./queries/data-export.queries";
import type { DataExportDataset, GeneratedDataExportDownload } from "./types/data-export.types";
import { getDataExportErrorMessage, isDataExportDataset } from "./utils/data-export.utils";

const PAGE_MAX_WIDTH_CLASS = "max-w-5xl";

const DataExportSearchSchema = z.object({
    weekOf: z.string().optional(),
    dataset: z.enum(DATA_EXPORT_DATASET_ORDER).optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_CALL_EXPORTS)({
    component: DataExportPage,
    validateSearch: zodValidator(DataExportSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "call_exports",
                to: APP_ROUTES_TO.PERFORMANCE_CALL_EXPORTS,
            },
            title: "call_exports",
        },
        permissions: [PERMISSIONS.CALL_DATA_EXPORT_DOWNLOAD],
    },
});

function DataExportPage() {
    return (
        <AuthGuard required={[PERMISSIONS.CALL_DATA_EXPORT_DOWNLOAD]} fallback={<NotAuthorizedPage />}>
            <DataExportContent />
        </AuthGuard>
    );
}

function resolveSelectedWeek(weekOf: string | undefined): DateTime {
    if (!weekOf) {
        return getLastCompletedWeekAnchor();
    }

    const parsed = DateTime.fromISO(weekOf);
    if (!parsed.isValid) {
        return getLastCompletedWeekAnchor();
    }

    return parsed.startOf("week");
}

function resolveSelectedDataset(dataset: string | undefined): DataExportDataset {
    if (dataset && isDataExportDataset(dataset)) {
        return dataset;
    }

    return DATA_EXPORT_DATASETS.CALLS;
}

function DataExportContent() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });

    const selectedWeek = useMemo(() => resolveSelectedWeek(search.weekOf), [search.weekOf]);
    const selectedDataset = useMemo(() => resolveSelectedDataset(search.dataset), [search.dataset]);
    const weekOf = selectedWeek.toISODate();

    const [generatedDownload, setGeneratedDownload] = useState<GeneratedDataExportDownload | null>(null);

    const {
        mutate: generateDownloadUrl,
        reset: resetGeneratedDownload,
        isPending,
        isError,
        error,
    } = useMutation({
        mutationFn: getDataExportDownloadUrl,
        onMutate: () => {
            setGeneratedDownload(null);
        },
        onSuccess: (response) => {
            setGeneratedDownload({
                response,
                expiresAtMs: Date.now() + response.expiresInSeconds * 1000,
            });
        },
    });

    const handleWeekChange = useCallback(
        (date: DateTime) => {
            setGeneratedDownload(null);
            resetGeneratedDownload();
            void navigate({
                search: {
                    weekOf: date.toISODate() ?? undefined,
                    dataset: selectedDataset,
                },
                replace: true,
            });
        },
        [navigate, selectedDataset]
    );

    const handleDatasetChange = useCallback(
        (dataset: DataExportDataset) => {
            setGeneratedDownload(null);
            resetGeneratedDownload();
            void navigate({
                search: {
                    weekOf: weekOf ?? undefined,
                    dataset,
                },
                replace: true,
            });
        },
        [navigate, weekOf]
    );

    const handleGenerate = useCallback(() => {
        if (!weekOf) {
            return;
        }

        generateDownloadUrl({
            dataset: selectedDataset,
            weekOf,
        });
    }, [selectedDataset, weekOf]);

    const errorMessage = isError ? getDataExportErrorMessage(error, t) : null;

    return (
        <div className={`mx-auto flex w-full ${PAGE_MAX_WIDTH_CLASS} flex-col gap-6 px-1 py-1 sm:px-2 sm:py-2`}>
            <section className={`${REPORT_SURFACE_CLASS} flex flex-col gap-6 p-5 sm:p-6`}>
                <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                        <ReportsWeekSelect selectedDate={selectedWeek} onDateChange={handleWeekChange} />
                    </div>
                    <DataExportDatasetSelect
                        dataset={selectedDataset}
                        onDatasetChange={handleDatasetChange}
                        disabled={isPending}
                    />
                </div>

                <p className="text-sm text-muted-foreground">{t("call_exports_hint")}</p>

                <Button
                    type="button"
                    onClick={handleGenerate}
                    disabled={!weekOf || isPending}
                    className="h-12 w-full rounded-xl sm:w-auto sm:min-w-48"
                >
                    {isPending ? (
                        <>
                            <Icon icon={TbLoader2} className="animate-spin" />
                            {t("call_exports_generating")}
                        </>
                    ) : (
                        t("call_exports_generate_link")
                    )}
                </Button>

                {errorMessage ? <p className="text-sm font-medium text-destructive">{errorMessage}</p> : null}

                {generatedDownload ? <DataExportResult generatedDownload={generatedDownload} /> : null}
            </section>
        </div>
    );
}
